// CardCarousel — generic horizontal scroll-snap rail with the same
// chevron-arrow navigation used by `XalucaOriginalsCarousel`. Drop any
// list of pre-built cards inside; the wrapper only handles layout
// (widths, snap, arrows, edge fade). It never modifies the cards
// themselves.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const CardCarousel = ({
  items,
  renderItem,
  getKey = (item, index) => item?.slug || item?.key || item?.id || index,
  // Tailwind width classes for the card width at each breakpoint.
  // Defaults match the rest of the site's card rails.
  itemWidthClass = "w-[80vw] sm:w-[56vw] md:w-[42vw] lg:w-[30vw] xl:w-[24rem]",
  variant = "light",
  edgeFade = true,
  prevLabel = "Previous",
  nextLabel = "Next",
  testIdPrefix = "card-carousel",
}) => {
  const railRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const refreshArrows = useCallback(() => {
    const rail = railRef.current;
    if (!rail) return;
    setCanPrev(rail.scrollLeft > 8);
    setCanNext(rail.scrollLeft + rail.clientWidth < rail.scrollWidth - 8);
  }, []);

  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return undefined;
    refreshArrows();
    rail.addEventListener("scroll", refreshArrows, { passive: true });
    window.addEventListener("resize", refreshArrows);
    return () => {
      rail.removeEventListener("scroll", refreshArrows);
      window.removeEventListener("resize", refreshArrows);
    };
  }, [refreshArrows, items?.length]);

  const scrollByCards = (dir) => {
    const rail = railRef.current;
    if (!rail) return;
    const card = rail.querySelector("[data-carousel-card]");
    const step = card ? card.getBoundingClientRect().width + 24 : rail.clientWidth * 0.85;
    rail.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  if (!items || items.length === 0) return null;

  const isDark = variant === "dark";
  const fadeFrom = isDark ? "from-[#0B0807]" : "from-[#FDFBF7]";
  const fadeTo = isDark ? "to-[#0B0807]" : "to-[#FDFBF7]";
  const arrowCls = isDark
    ? "bg-[#1A1513] border-[#FDFBF7]/15 hover:border-[#D4A373] text-[#FDFBF7] hover:text-[#D4A373]"
    : "bg-[#FDFBF7] border-[#2C2621]/15 hover:border-[#C16542] text-[#2C2621] hover:text-[#C16542]";
  const arrowDisabledCls = isDark
    ? "bg-[#1A1513] border-[#FDFBF7]/8 text-[#FDFBF7]/25 cursor-not-allowed"
    : "bg-[#FDFBF7] border-[#2C2621]/8 text-[#2C2621]/25 cursor-not-allowed";

  return (
    <div className="relative" data-testid={testIdPrefix}>
      {/* Edge fade — optional cream/dark wash over the rail's left & right
          edges. Disable via `edgeFade={false}` when it reads as a white
          glow over the cards. */}
      {edgeFade ? (
        <>
          <div className={`pointer-events-none absolute inset-y-0 left-0 w-8 md:w-16 bg-gradient-to-r ${fadeFrom} to-transparent z-10 hidden md:block`} />
          <div className={`pointer-events-none absolute inset-y-0 right-0 w-8 md:w-16 bg-gradient-to-l ${fadeTo} to-transparent z-10 hidden md:block`} />
        </>
      ) : null}

      {/* Arrow buttons — hidden on mobile (touch-first); enabled on tablet+ */}
      <button
        type="button"
        onClick={() => scrollByCards(-1)}
        aria-label={prevLabel}
        disabled={!canPrev}
        className={`hidden md:flex absolute -left-2 lg:-left-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full border shadow-[0_8px_24px_-12px_rgba(0,0,0,0.5)] transition-all ${
          canPrev ? arrowCls : arrowDisabledCls
        }`}
        data-testid={`${testIdPrefix}-prev`}
      >
        <ChevronLeft className="w-5 h-5" strokeWidth={1.6} />
      </button>
      <button
        type="button"
        onClick={() => scrollByCards(1)}
        aria-label={nextLabel}
        disabled={!canNext}
        className={`hidden md:flex absolute -right-2 lg:-right-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full border shadow-[0_8px_24px_-12px_rgba(0,0,0,0.5)] transition-all ${
          canNext ? arrowCls : arrowDisabledCls
        }`}
        data-testid={`${testIdPrefix}-next`}
      >
        <ChevronRight className="w-5 h-5" strokeWidth={1.6} />
      </button>

      {/* Scroll-snap rail. Mobile users swipe; desktop users use arrows. */}
      <ul
        ref={railRef}
        className="flex gap-6 md:gap-8 overflow-x-auto pb-4 -mb-4 snap-x snap-mandatory scroll-px-6 md:scroll-px-12 px-1 md:px-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        data-testid={`${testIdPrefix}-rail`}
      >
        {items.map((item, index) => (
          <li
            key={getKey(item, index)}
            data-carousel-card
            className={`snap-start shrink-0 ${itemWidthClass}`}
            data-testid={`${testIdPrefix}-card-${index}`}
          >
            {renderItem(item, index)}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default CardCarousel;
